import { Controller } from "@hotwired/stimulus";

// The studio's real preview: each change sends the form as it stands to the
// server, which prints the report card without saving anything. Undo walks back
// through the form's states; black and white shows what a photocopy leaves.
export default class extends Controller {
  static targets = ["frame", "undo", "fit", "student", "range", "marks", "simulation", "simulationFrame", "download"];
  static values = { url: String };

  connect() {
    this.history = [];
    this.current = this.snapshot();
    this.element.addEventListener("block-order:moved", this.onMoved);
    this.rangeTargets.forEach((range) => this.addValue(range));
    this.loadMarks();
    this.refresh();
  }

  disconnect() {
    clearTimeout(this.timer);
    this.element.removeEventListener("block-order:moved", this.onMoved);
  }

  onMoved = () => this.changed();

  // Each slider says its value, while it moves.
  addValue(range) {
    const value = document.createElement("output");
    value.style.cssText = "flex:none; min-width:42px; text-align:right; font-weight:600; font-variant-numeric:tabular-nums;";
    range.after(value);
    this.write(range);
  }

  write(range) {
    range.nextElementSibling.textContent = `${range.value} ${range.dataset.unit}`.trim();
  }

  showValue(event) {
    if (event.target.type === "range" && event.target.dataset.unit !== undefined) this.write(event.target);
  }

  changed() {
    const state = this.snapshot();
    if (JSON.stringify(state) !== JSON.stringify(this.current)) {
      this.history.push(this.current);
      this.current = state;
      this.undoButtonState();
    }
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), 350);
  }

  undo() {
    const state = this.history.pop();
    if (!state) return;

    this.restore(state);
    this.current = state;
    this.undoButtonState();
    this.refresh();
  }

  blackAndWhite() {
    this.frameTarget.style.filter = this.frameTarget.style.filter ? "" : "grayscale(1) contrast(1.1)";
  }

  // Another student of the class: the longest name or class is what breaks a page.
  nextStudent() {
    this.studentTarget.value = Number(this.studentTarget.value) + 1;
    this.refresh();
  }

  // The print fits the sheet by shrinking its content: say when it has to.
  loaded() {
    setTimeout(() => {
      const layer = this.frameTarget.contentDocument?.querySelector(".content-layer");
      if (!layer || !this.hasFitTarget) return;

      const zoom = parseFloat(layer.style.zoom) || 1;
      if (!layer.style.zoom) this.fitTarget.style.removeProperty("color");
      const shrunk = zoom < 0.97;
      this.fitTarget.textContent = shrunk ? `${this.fitTarget.dataset.shrunk} ${Math.round(zoom * 100)} %` : this.fitTarget.dataset.fits;
      this.fitTarget.style.color = shrunk ? "var(--g4s-warning)" : "var(--g4s-success)";
    }, 300);
  }

  zoom(event) {
    this.write(event.currentTarget);
    this.frameTarget.style.zoom = event.currentTarget.value / 100;
  }

  // The simulation's marks belong to a class: its subjects change with it.
  async loadMarks() {
    if (!this.hasMarksTarget) return;

    const classroom = this.form.querySelector('[name="preview_classroom_id"]').value;
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("simulation_marks", "1");
    url.searchParams.set("preview_classroom_id", classroom);
    const response = await fetch(url, { credentials: "same-origin" });
    this.marksTarget.innerHTML = response.ok ? await response.text() : "";
  }

  async refresh() {
    const response = await this.post(this.formBody());
    if (response.headers.get("Content-Type")?.includes("application/pdf")) await this.showPdf(response);
    else this.frameTarget.srcdoc = response.ok ? await response.text() : "";
    if (this.simulationOpen) this.refreshSimulation();
  }

  // The lists and the cards are drawn by Prawn: the preview is their real PDF.
  async showPdf(response) {
    const previous = this.frameTarget.src;
    this.frameTarget.src = `${URL.createObjectURL(await response.blob())}#toolbar=0&navpanes=0&view=FitH`;
    if (previous.startsWith("blob:")) URL.revokeObjectURL(previous.split("#")[0]);
  }

  // The large window: the marks beside their report card, ready to download.
  openSimulation() {
    this.simulationTarget.showModal();
    this.refreshSimulation();
  }

  closeSimulation() {
    this.simulationTarget.close();
  }

  async refreshSimulation() {
    const response = await this.post(this.simulationBody());
    this.simulationFrameTarget.srcdoc = response.ok ? await response.text() : "";
  }

  async download() {
    const body = this.simulationBody();
    body.set("download", "1");
    this.downloadTarget.disabled = true;
    const response = await this.post(body);
    this.downloadTarget.disabled = false;
    if (!response.ok) return;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(await response.blob());
    link.download = `${body.get("preview_document")}-simulation.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  // private

  get simulationOpen() {
    return this.hasSimulationTarget && this.simulationTarget.open;
  }

  formBody() {
    const body = new FormData(this.form);
    body.delete("_method");
    // Files chosen but not saved never reach the preview: do not upload them at each change.
    [...body.keys()].filter((key) => body.get(key) instanceof File).forEach((key) => body.delete(key));
    return body;
  }

  simulationBody() {
    const body = this.formBody();
    body.set("simulate", "1");
    if (["annual", "class"].includes(body.get("preview_document"))) body.set("preview_document", "period");
    return body;
  }

  post(body) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(this.urlValue, { method: "POST", body, headers: { "X-CSRF-Token": token }, credentials: "same-origin" });
  }

  get form() {
    return this.element.querySelector("form");
  }

  undoButtonState() {
    if (this.hasUndoTarget) this.undoTarget.disabled = this.history.length === 0;
  }

  snapshot() {
    return {
      blocks: [...this.form.querySelectorAll('[name="appearance[blocks][]"], [name="appearance[summary_order][]"], [name="documents[blocks][]"]')].map((input) => input.value),
      fields: this.settingFields()
        .map((input) => (input.type === "checkbox" || input.type === "radio" ? input.checked : input.value)),
    };
  }

  settingFields() {
    return [...this.form.querySelectorAll("input, select, textarea")]
      .filter((input) => /^(appearance|documents|template)/.test(input.name) && input.type !== "hidden");
  }

  restore(state) {
    const rows = [...this.form.querySelectorAll("[data-block-order-target='row']")];
    state.blocks.forEach((block) => {
      const row = rows.find((candidate) => candidate.querySelector('input[type="hidden"]').value === block);
      row.parentNode.appendChild(row);
    });
    this.settingFields()
      .forEach((input, index) => {
        if (input.type === "checkbox" || input.type === "radio") input.checked = state.fields[index];
        else input.value = state.fields[index];
      });
    this.rangeTargets.forEach((range) => this.write(range));
  }
}
